/*
 * Header behaviour, modelled on lonefox.com (see ud-header.css for the styling side).
 *
 * The menu bar is always solid. These classes on <html> control the centered logo bar:
 *   ud-hdr-solid  - the logo bar has left its see-through state (anything except "hero page, at the top,
 *                   nothing hovered or open"); on a hero page the white logo only shows while this is off
 *   ud-logo-solid - the centered logo bar has a solid white background (scrolled, or hovered/open on a hero page)
 *   ud-overlay-open - the mobile menu or search panel is open (the logo bar fades out so it can't cover the panel)
 * and keeps --ud-header-bottom in sync with the real header position.
 *
 * It also swaps the "Custom" menu item for "Furniture" and gives it a dropdown (see swapMenuItem below).
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var SCROLL_THRESHOLD = 15; // px scrolled before the header turns solid (lonefox.com flips between 10 and 20)
  var hovered = false;
  var focused = false;
  var frame = 0;

  function find(selector) {
    return document.querySelector(selector);
  }

  function isHeroPage() {
    return getComputedStyle(root).getPropertyValue('--ud-hero-page').trim() === '1';
  }

  function overlayOpen() {
    // mobile menu drawer and the search modal are <details> elements inside the header
    return !!find('.section-header details[open]');
  }

  function measure() {
    var bar = find('.section-header .header-wrapper');
    if (!bar) return;
    var rect = bar.getBoundingClientRect();
    root.style.setProperty('--ud-header-bottom', Math.max(0, Math.round(rect.bottom)) + 'px');
  }

  function update() {
    frame = 0;
    measure();

    var hero = isHeroPage();
    var scrolled = window.scrollY > SCROLL_THRESHOLD;
    var open = overlayOpen();
    var engaged = hovered || focused || open;
    var clear = hero && !scrolled && !engaged;

    root.classList.toggle('ud-overlay-open', open);
    root.classList.toggle('ud-hdr-solid', !clear);
    root.classList.toggle('ud-logo-solid', scrolled || (hero && engaged));
  }

  function schedule() {
    if (!frame) frame = window.requestAnimationFrame(update);
  }

  // ---------- Menu: "Custom" becomes "Furniture", "Bestsellers" becomes "Rugs" (plain links) ----------
  // The menu comes from the Qikify Smart Menu app, which keeps its own copy of the menu outside the theme,
  // so a theme draft can't change it. Until the items are renamed in the app, this swaps them on the page: the
  // label, the link (the click is handled here because the app blocks links on items that have a dropdown),
  // and ud-header.css hides the dropdown the app adds on hover. Neither item has a dropdown of its own any more.
  // (Furniture used to get one from <template id="ud-furniture-menu">; to bring it back, put dropdown: true on
  // its entry below. The panel is still in sections/ud-header-menu.liquid and styled in assets/ud-menu.css.)
  // The item is found by its label ("Custom", or "Furniture" once the app says so). The marker is an
  // attribute because the app resets the item's classes every time it is hovered.
  var MENU_SWAPS = [
    { from: 'custom', label: 'FURNITURE', url: '/collections/furniture' },
    { from: 'bestsellers', label: 'RUGS', url: '/collections/rugs' }
  ];

  function swapMenuItem() {
    var items = document.querySelectorAll('.section-header .tmenu_item_level_0');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var link = item.querySelector(':scope > .tmenu_item_link');
      var text = link && link.querySelector('.tmenu_item_text');
      if (!text) continue;
      var label = text.textContent.trim().toLowerCase();
      var swap = null;
      for (var j = 0; j < MENU_SWAPS.length; j++) {
        if (label === MENU_SWAPS[j].from || label === MENU_SWAPS[j].label.toLowerCase()) swap = MENU_SWAPS[j];
      }
      if (!swap) continue;

      if (label === swap.from) text.textContent = swap.label;
      // Always the lowercase address, also once the app has the new name (it may be typed "/collections/Rugs").
      if (link.getAttribute('href') !== swap.url) link.setAttribute('href', swap.url);
      // The app also puts the old name in the link's tooltip.
      if ((link.getAttribute('title') || '').trim().toLowerCase() === swap.from) {
        link.setAttribute('title', swap.label);
      }
      link.setAttribute('data-ud-menu-link', '');
      item.setAttribute('data-ud-menu', 'plain');
      item.classList.remove('tmenu_item_has_child');
      if (swap.dropdown) {
        addFurnitureMenu(item, link);
      } else {
        // A plain link: screen readers shouldn't be told it opens a menu (the app marks items that have children).
        link.removeAttribute('aria-haspopup');
        link.removeAttribute('aria-expanded');
      }
    }
  }

  function addFurnitureMenu(item, link) {
    var template = document.getElementById('ud-furniture-menu');
    if (!template || !template.content || item.querySelector(':scope > .ud-submenu')) return;
    var panel = template.content.firstElementChild;
    if (!panel) return;
    item.appendChild(panel.cloneNode(true));
    if (item.closest('header-drawer')) link.setAttribute('aria-expanded', 'false');
  }

  // ---------- Logo: "ud / shop" opens a list of the other Upstate Down sites ----------
  // Same idea as "ud / real estate" on upstatedownrealestate.com. The links live in <template id="ud-brand-menu">
  // (sections/ud-header-menu.liquid, editable in the theme editor); the look is at the end of assets/ud-menu.css.
  // Desktop: a chevron next to the small logo; the list opens on hover, keyboard focus or a click on the chevron.
  // Phones don't show the small logo, so the same links go into the menu drawer, above Log in.
  function addBrandMenu() {
    var template = document.getElementById('ud-brand-menu');
    if (!template || !template.content) return;
    var list = template.content.firstElementChild;
    if (!list) return;

    // The small logo link is inside <h1 class="header__heading"> on the home page but sits directly in the header
    // grid on every other page, so it is the link itself that gets wrapped (the wrapper takes its grid place).
    var logo = find('.section-header .header__heading-link');
    if (logo && !logo.closest('.ud-brand')) {
      var wrap = document.createElement('span');
      wrap.className = 'ud-brand';
      logo.parentNode.insertBefore(wrap, logo);
      wrap.appendChild(logo);
      wrap.insertAdjacentHTML(
        'beforeend',
        '<button type="button" class="ud-brand__toggle" aria-expanded="false" aria-controls="ud-brand-panel">' +
        '<svg class="ud-brand__chev" viewBox="0 0 10 6" aria-hidden="true" focusable="false">' +
        '<path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1"/></svg></button>'
      );
      wrap.querySelector('button').setAttribute('aria-label', template.getAttribute('data-label') || 'More from Upstate Down');
      var panel = document.createElement('div');
      panel.className = 'ud-brand__panel';
      panel.id = 'ud-brand-panel';
      panel.appendChild(list.cloneNode(true));
      wrap.appendChild(panel);
      wireBrand(wrap);
    }

    var utility = find('.section-header header-drawer .menu-drawer__utility-links');
    var drawerHost = utility || find('.section-header header-drawer .menu-drawer__navigation-container');
    if (drawerHost && !find('.section-header header-drawer .ud-brand-drawer')) {
      var block = document.createElement('div');
      block.className = 'ud-brand-drawer';
      var label = document.createElement('p');
      label.className = 'ud-brand-drawer__label';
      label.textContent = template.getAttribute('data-title') || 'Also from Upstate Down';
      block.appendChild(label);
      block.appendChild(list.cloneNode(true));
      if (utility) utility.parentNode.insertBefore(block, utility);
      else drawerHost.appendChild(block);
    }
  }

  function wireBrand(wrap) {
    var button = wrap.querySelector('.ud-brand__toggle');
    function setOpen(open) {
      if (open) wrap.setAttribute('data-open', '');
      else wrap.removeAttribute('data-open');
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    button.addEventListener('click', function (event) {
      event.preventDefault();
      setOpen(!wrap.hasAttribute('data-open'));
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && wrap.hasAttribute('data-open')) {
        setOpen(false);
        button.focus();
      }
    });
    document.addEventListener('click', function (event) {
      if (!wrap.contains(event.target)) setOpen(false);
    });
  }

  window.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-ud-menu-link]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var item = link.closest('[data-ud-menu="plain"]');
    // In the phone menu the item opens its list (the first link in it goes to the furniture page).
    if (item && item.closest('header-drawer') && item.querySelector(':scope > .ud-submenu')) {
      var open = !item.hasAttribute('data-ud-open');
      if (open) item.setAttribute('data-ud-open', '');
      else item.removeAttribute('data-ud-open');
      link.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }
    window.location.assign(link.getAttribute('href'));
  }, true);

  function inHeader(node) {
    return !!(node && node.closest && node.closest('.section-header, .ud-logo-link'));
  }

  // Pointer hover (mouse only, so touch taps don't leave the header stuck solid).
  document.addEventListener('pointerover', function (event) {
    if (event.pointerType === 'mouse' && inHeader(event.target) && !hovered) {
      hovered = true;
      schedule();
    }
  });
  document.addEventListener('pointerout', function (event) {
    if (event.pointerType === 'mouse' && inHeader(event.target) && !inHeader(event.relatedTarget) && hovered) {
      hovered = false;
      schedule();
    }
  });

  // Keyboard focus inside the header counts as hover.
  document.addEventListener('focusin', function (event) {
    if (inHeader(event.target) && event.target.matches && event.target.matches(':focus-visible')) {
      focused = true;
      schedule();
    }
  });
  document.addEventListener('focusout', function (event) {
    if (inHeader(event.target) && focused) {
      focused = false;
      schedule();
    }
  });

  // Mobile menu / search opening and closing.
  document.addEventListener('toggle', schedule, true);

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('load', schedule);
  window.addEventListener('pageshow', schedule);
  // The dropdown's content sits further down the page than the script, so try again once everything is in.
  window.addEventListener('load', function () {
    swapMenuItem();
    addBrandMenu();
  });
  document.addEventListener('shopify:section:load', schedule);

  var bar = find('.section-header .header-wrapper');
  if (bar && 'ResizeObserver' in window) new ResizeObserver(schedule).observe(bar);

  // The app builds its menu after the page loads (and again for the mobile drawer), so watch the header.
  var headerSection = find('.section-header');
  if (headerSection && 'MutationObserver' in window) {
    new MutationObserver(function () {
      swapMenuItem();
      addBrandMenu();
    }).observe(headerSection, { childList: true, subtree: true });
  }
  swapMenuItem();
  addBrandMenu();

  update();
  // Enable transitions only after the first state is on screen.
  window.requestAnimationFrame(function () {
    window.requestAnimationFrame(function () {
      root.classList.add('ud-hdr-ready');
    });
  });
})();
